import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, } from 'react';
import { Input, Select } from 'antd';
import { fetchAllUsersSimplified } from 'api/admin/users-api';
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import clsx from 'clsx';
import { fetchCurrentTeamMembers } from 'api/team-api';
import styles from './style.module.less';
const emailFormat = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const UserSearchInput = ({ label, value, onChange, onPressEnter, autoFocus, projectsToShare, }) => {
    const { session } = useSession();
    const [allUsers, setAllUsers] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const isTeamProject = projectsToShare.some((project) => project.isTeamProject);
    const createValidationMessage = (validity) => {
        if (validity === 'invalid') {
            return txt('emailValidation');
        }
        if (validity === 'self') {
            return txt('selfSharing');
        }
        if (validity === 'not-found') {
            return txt('notFoundUser');
        }
        if (validity === 'my-open-team') {
            if (projectsToShare.length === 1) {
                return txt('cannotShareOneToOpenTeam');
            }
            return txt('cannotShareMoreToOpenTeam');
        }
        return null;
    };
    useEffect(() => {
        const fetchUsers = async () => setAllUsers((await fetchAllUsersSimplified(session.connection))
            .list.filter((user) => { var _a; return ((_a = user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== session.login.user.email.toLowerCase(); }));
        const fetchTeamMembers = async () => setTeamMembers((await fetchCurrentTeamMembers(session.connection)).list);
        if (session.login.hasClaim('admin:superadmin')) {
            void fetchUsers();
        }
        void fetchTeamMembers();
    }, []);
    const validateEmail = (input) => {
        const email = input.trim();
        if (email === '' || !emailFormat.test(email)) {
            return 'invalid';
        }
        if (email.toLowerCase() === session.login.user.email.toLowerCase()) {
            return 'self';
        }
        if (isTeamProject && teamMembers.map((member) => member.email.toLowerCase()).includes(email)) {
            return 'my-open-team';
        }
        return 'valid';
    };
    const filterUsersByPrefix = (users, prefix) => {
        if (isTeamProject && session.login.hasClaim('teamProjects:enabled')) {
            return users.filter((user) => user.teamId !== session.login.user.teamId)
                .filter((user) => { var _a; return (_a = user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith(prefix); });
        }
        return users.filter((user) => { var _a; return (_a = user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().startsWith(prefix); });
    };
    const handleInputChange = (event) => {
        const email = event.target.value;
        const validity = validateEmail(email);
        onChange({ email, validity });
    };
    const handleSelectChange = (email) => {
        onChange({ email, validity: validateEmail(email) });
    };
    const handleSelectSearch = (searchTerm) => {
        const cleaned = searchTerm.toLowerCase().trim();
        if (cleaned.length < 2) {
            onChange({ email: searchTerm, validity: 'unchecked' });
            return;
        }
        const validity = validateEmail(searchTerm);
        if (validity === 'self' || validity === 'my-open-team') {
            onChange({ email: searchTerm, validity });
            return;
        }
        const users = filterUsersByPrefix(allUsers, cleaned);
        setFilteredUsers(users);
        if (users.length === 0) {
            onChange({ email: searchTerm, validity: validity === 'invalid' ? 'invalid' : 'not-found' });
        }
        else {
            onChange({ email: searchTerm, validity: 'unchecked' });
        }
    };
    const inputField = session.login.hasClaim('admin:superadmin')
        ? (_jsx(Select, { className: styles.searchField, showSearch: true, placeholder: txt('email'), filterOption: false, onSearch: handleSelectSearch, onChange: handleSelectChange, notFoundContent: null, autoFocus: autoFocus, children: filteredUsers.map((user) => (_jsx(Select.Option, { value: user.email, children: user.email }, user.id))) })) : (_jsx(Input, { className: styles.searchField, placeholder: txt('email'), value: value.email, onChange: handleInputChange, required: true, autoFocus: autoFocus, onPressEnter: onPressEnter }));
    const searchType = session.login.hasClaim('admin:superadmin')
        ? 'userSearchSelect'
        : 'userSearchTextfield';
    const validationMessage = createValidationMessage(value.validity);
    return (_jsxs("div", { className: clsx(styles.userSearch, styles[searchType]), children: [_jsxs("div", { className: styles.label, children: [label, ":"] }), _jsxs("div", { className: styles.input, children: [inputField, validationMessage === null
                        ? null
                        : _jsx("p", { className: styles.validationMessage, children: validationMessage })] })] }));
};
export default UserSearchInput;
